import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiRequest, type ApiError } from "@/shared/api";
import { normalizeMembershipLevelsResponse } from "@/features/plan/services/normalize-membership-levels";

type ApiQueryOptions<TData> = Omit<
  UseQueryOptions<TData, ApiError, TData, readonly unknown[]>,
  "queryKey" | "queryFn"
>;

export type MembershipStatusResponse = {
  status: string;
  plan: {
    name: string;
    type: string;
    billingType: string | null;
    amount: number | null;
    currency: string | null;
  } | null;
  paymentUrl?: string | null;
};

export type MembershipLevel = {
  id: string;
  planCode?: string;
  name: string;
  level?: number;
  monthlyPrice?: number;
  annualPrice?: number;
  annualMonthlyPrice?: number;
  cost?: number | string[] | null;
  setupFee?: number | null;
  description: string;
  access?: string[];
  education?: string[];
  fees?: string[];
  isPopular?: boolean;
  config?: {
    level?: number;
    access?: string[];
    condition?: string;
  };
};

const membershipStatusRequest = {
  url: "/profile/membership",
  method: "get" as const,
};

const membershipLevelsRequest = {
  url: "/membership/levels",
  method: "get" as const,
};

export const getMembershipStatus = () =>
  apiRequest<MembershipStatusResponse>(membershipStatusRequest);

export const getMembershipLevels = async (): Promise<MembershipLevel[]> =>
  normalizeMembershipLevelsResponse(
    await apiRequest<unknown>(membershipLevelsRequest)
  );

export const useMembershipStatus = (
  options?: ApiQueryOptions<MembershipStatusResponse>
) =>
  useQuery<MembershipStatusResponse, ApiError>({
    queryKey: ["membership-status"],
    queryFn: getMembershipStatus,
    ...options,
  });

export const useMembershipLevels = (
  options?: ApiQueryOptions<MembershipLevel[]>
) =>
  useQuery<MembershipLevel[], ApiError>({
    queryKey: ["membership-levels"],
    queryFn: getMembershipLevels,
    ...options,
  });
